import { getUserVisibleOrDefaultExceptionMessage, ReadFile } from '@paragon/client-tools';
import { ProductionComponentInstance, ProductionGroup } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { componentDesignToLayoutModel, nonSynchronizedLayoutModelLoaded } from '@paragon/layout-models';
import { getLoggedInUserOrganization } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { SettableProductionGroupProperties } from '../../components/dialogs/ProductionGroupProperties';
import { getAnalysisSetGuidsByProductionGroupGuid } from '../selectors/getAnalysisSetGuidsByProductionGroupGuid';
import { getAnalysisSetGuidToComponentDesignName } from '../selectors/getAnalysisSetGuidToComponentDesignName';
import { getComponentDesignGuidToMostRecentAnalysisSetGuidMap } from '../selectors/getComponentDesignGuidToMostRecentAnalysisSetGuidMap';
import { analysisHistoryLoaded } from '../slices/analysisHistorySlice';
import { batchesLoaded } from '../slices/batchesSlice';
import { bundlesLoaded } from '../slices/bundlesSlice';
import { componentDesignTimestampsLoaded } from '../slices/componentDesignTimestamps';
import { productionComponentInstancesLoaded } from '../slices/productionComponentInstancesSlice';
import {
  productionGroupCreated,
  productionGroupDeleted,
  productionGroupsLoaded,
} from '../slices/productionGroupsSlice';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export function loadProductionGroupsForProject(projectGuid: string): AsyncThunkActionVoid {
  return async function loadProductionGroupsForProjectThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Loading production groups for project...'));

    try {
      const productionGroupsAlreadyLoaded = Object.values(getState().productionGroups).some(
        (productionGroup) => productionGroup.projectGuid === projectGuid,
      );
      if (productionGroupsAlreadyLoaded) return;

      const productionGroupsForProject = await designService.productionGroups.getForProject(projectGuid);
      dispatch(productionGroupsLoaded(productionGroupsForProject));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading production groups'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function loadProductionGroupData(productionGroupGuid: string): AsyncThunkActionVoid {
  return async function loadProductionGroupDataThunkAction(dispatch, getState) {
    const productionGroupDataAlreadyLoaded = Object.values(getState().productionComponentInstances).some(
      (productionComponentInstance) => productionComponentInstance.productionGroupGuid === productionGroupGuid,
    );
    if (productionGroupDataAlreadyLoaded) return;

    const loadBatchesPromise = dispatch(loadBatchesForProductionGroup(productionGroupGuid));
    const loadBundlesPromise = dispatch(loadBundlesForProductionGroup(productionGroupGuid));
    await dispatch(loadProductionComponentInstancesForProductionGroup(productionGroupGuid));
    await Promise.all([loadBatchesPromise, loadBundlesPromise]);
  };
}

function loadBatchesForProductionGroup(productionGroupGuid: string): AsyncThunkActionVoid {
  return async function loadBatchesForProjectThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Loading batches for production group...'));

    try {
      const state = getState();

      if (Object.values(state.batches).some((batch) => batch.productionGroupGuid === productionGroupGuid)) return;

      const batchesForProject = await designService.batches.getForProductionGroup(productionGroupGuid);
      dispatch(batchesLoaded(batchesForProject));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading batches'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

function loadBundlesForProductionGroup(productionGroupGuid: string): AsyncThunkActionVoid {
  return async function loadBundlesForBatchesThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Loading bundles for production group...'));

    try {
      const state = getState();

      if (Object.values(state.bundles).some((bundle) => bundle.productionGroupGuid === productionGroupGuid)) return;

      const bundlesForProductionGroup = await designService.bundles.getForProductionGroup(productionGroupGuid);
      dispatch(bundlesLoaded(bundlesForProductionGroup));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading bundles'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

function loadProductionComponentInstancesForProductionGroup(
  productionGroupGuid: string,
): AsyncThunkActionReturn<readonly ProductionComponentInstance[]> {
  return async function loadProductionComponentInstancesForProductionGroupThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Loading components for production group...'));

    try {
      const state = getState();

      const productionComponentInstancesInState = Object.values(state.productionComponentInstances).filter(
        (productionComponentInstance) => productionComponentInstance.productionGroupGuid === productionGroupGuid,
      );
      if (productionComponentInstancesInState.length > 0) return productionComponentInstancesInState;

      const productionComponentInstances =
        await designService.productionComponentInstances.getForProductionGroup(productionGroupGuid);

      dispatch(productionComponentInstancesLoaded(productionComponentInstances));
      return productionComponentInstances;
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading components'));
      return [];
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

/**
 * This is a specialized method for use with replacing production components. It is expecting `getProductionGroupData`
 * to already have been called for the same production group GUID. It is separate because it does more weighty loading
 * calls. This function will identify analysis set GUIDs not loaded with the project
 */
export function loadProductionGroupDataForOrphanedInstances(productionGroupGuid: string): AsyncThunkActionVoid {
  return async function loadProductionGroupDataForOrphanedInstancesThunkAction(dispatch, getState) {
    const analysisSetGuidsForProductionGroup =
      getAnalysisSetGuidsByProductionGroupGuid(getState())[productionGroupGuid];
    const analysisSetGuidToComponentDesignName = getAnalysisSetGuidToComponentDesignName(getState());
    const analysisSetGuidsNotLoaded = analysisSetGuidsForProductionGroup.filter(
      (analysisSetGuid) => !(analysisSetGuid in analysisSetGuidToComponentDesignName),
    );
    const componentDesignsWithInformationByAnalysisSetGuid =
      await designService.analysisSets.fetchComponentsFromAnalysisSets(analysisSetGuidsNotLoaded);
    const componentDesignsWithInformation = Object.values(componentDesignsWithInformationByAnalysisSetGuid);
    dispatch(componentDesignTimestampsLoaded(componentDesignsWithInformation.map(({ timestamps }) => timestamps)));
    dispatch(analysisHistoryLoaded(componentDesignsWithInformation.flatMap(({ history }) => history)));
    const layoutModels = componentDesignsWithInformation.map(({ componentDesign }) =>
      componentDesignToLayoutModel(componentDesign),
    );
    dispatch(nonSynchronizedLayoutModelLoaded(layoutModels));
  };
}

export function createProductionGroup(
  projectGuid: string,
  productionGroupProperties: Pick<
    ProductionGroup,
    'projectId' | 'projectShortName' | 'buildingType' | 'buildingInstance' | 'level'
  >,
  quantityMap: { [componentDesignGuid: string]: number },
): AsyncThunkActionVoid {
  return async function createProductionGroupThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Creating production group...'));

    try {
      const productionGroupGuid = crypto.randomUUID();
      const productionGroup: ProductionGroup = {
        ...productionGroupProperties,
        guid: productionGroupGuid,
        projectGuid,
        organization: getLoggedInUserOrganization(getState())!,
      };
      await designService.productionGroups.insert(productionGroup);
      dispatch(productionGroupCreated(productionGroup));
      dispatch(createProductionComponentInstancesFromQuantityMap(quantityMap, productionGroupGuid));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error creating production group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createProductionComponentInstancesFromQuantityMap(
  quantityMap: {
    [componentDesignGuid: string]: number;
  },
  productionGroupGuid: string,
): AsyncThunkActionVoid {
  return async function createProductionComponentInstancesFromQuantityMapThunkAction(dispatch, getState) {
    const componentDesignGuidToMostRecentAnalysisSetGuidMap =
      getComponentDesignGuidToMostRecentAnalysisSetGuidMap(getState());
    const productionComponentInstances: ProductionComponentInstance[] = quantityMapToProductionComponentInstances(
      quantityMap,
      componentDesignGuidToMostRecentAnalysisSetGuidMap,
      productionGroupGuid,
    );
    await designService.productionComponentInstances.insert(productionComponentInstances);
  };
}

function quantityMapToProductionComponentInstances(
  quantityMap: {
    [componentDesignGuid: string]: number;
  },
  componentDesignGuidToMostRecentAnalysisSetGuidMap: { [componentDesignGuid: string]: string | null },
  productionGroupGuid: string,
) {
  return Object.entries(quantityMap).flatMap(([componentDesignGuid, quantity]) => {
    const productionComponentInstancesForQuantity: ProductionComponentInstance[] = [];
    for (let i = 0; i < quantity; i += 1) {
      const analysisSetGuid = componentDesignGuidToMostRecentAnalysisSetGuidMap[componentDesignGuid];
      if (!analysisSetGuid) throw new Error('No analysis set GUID found to use for new production component instances');
      productionComponentInstancesForQuantity.push({
        guid: crypto.randomUUID(),
        analysisSetGuid,
        productionGroupGuid,
        batchGuid: null,
        bundleGuid: null,
        bundlePositionX: null,
        bundlePositionY: null,
        rebuildOf: null,
        status: 'Unscheduled',
      });
    }
    return productionComponentInstancesForQuantity;
  });
}

export function createProductionGroupFromCnt(
  projectGuid: string,
  productionGroupProperties: Pick<
    ProductionGroup,
    'projectId' | 'projectShortName' | 'buildingType' | 'buildingInstance' | 'level'
  >,
  file: ReadFile,
): AsyncThunkActionVoid {
  return async function createProductionGroupFromCntThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Creating production group and batches from count file...'));

    try {
      const cntContents = atob(file.contents.split(',')[1]);
      const { productionGroup } = await designService.productionGroups.createProductionGroupFromCnt({
        projectGuid,
        cntContents,
        ...productionGroupProperties,
      });
      dispatch(productionGroupsLoaded([productionGroup]));
    } catch (error) {
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(
            error,
            'Unable to create production group and/or batches from count file',
          ),
        ),
      );
      console.error(error);

      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function updateProductionGroupProperties(
  productionGroupProperties: SettableProductionGroupProperties,
  productionGroupGuid: string,
): AsyncThunkActionVoid {
  return async function updateProductionGroupPropertiesThunkAction(dispatch, getState) {
    const { productionGroups } = getState();
    const updatedProductionGroup: ProductionGroup = {
      ...productionGroups[productionGroupGuid],
      ...productionGroupProperties,
    };

    const spinnerId = dispatch(addSpinnerMessage('Creating production group...'));

    try {
      await designService.productionGroups.update(updatedProductionGroup);
      dispatch(productionGroupsLoaded([updatedProductionGroup]));
    } catch (e) {
      console.error(e);
      dispatch(enqueueError('Error updating production group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeProductionGroup(productionGroupGuid: string): AsyncThunkActionVoid {
  return async function removeProductionGroupThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Deleting production group...'));
    try {
      await designService.productionGroups.remove(productionGroupGuid);
      dispatch(productionGroupDeleted(productionGroupGuid));
    } catch (e) {
      console.error(e);
      dispatch(enqueueError('Error deleting production group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function duplicateProductionGroup(
  productionGroupProperties: Pick<
    ProductionGroup,
    'guid' | 'projectId' | 'projectShortName' | 'buildingType' | 'buildingInstance' | 'level'
  >,
): AsyncThunkActionReturn<ProductionGroup | null> {
  return async function duplicateProductionGroupThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Duplicating production group...'));

    try {
      const { productionGroup, batches, bundles, productionComponentInstances } =
        await designService.productionGroups.duplicate(productionGroupProperties);
      dispatch(productionGroupsLoaded([productionGroup]));
      dispatch(batchesLoaded(batches));
      dispatch(bundlesLoaded(bundles));
      dispatch(productionComponentInstancesLoaded(productionComponentInstances));
      return productionGroup;
    } catch (error) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Unable to duplicate production group')));
      console.error(error);
      return null;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
